import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Pause, Ban, RotateCcw, Printer, UserPlus, Percent, Gift, Truck } from 'lucide-react'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { usePOSConfig } from '@/api/config'
import { usePrintReceipt } from '@/api/payments'
import {
  useHoldCurrentReceipt,
  useVoidCurrentReceipt,
  useSelectCustomer,
} from '@/hooks'
import { AddCustomerModal } from '@/components/customers/AddCustomerModal'
import type { Customer } from '@/types'

// Mobile-only Quick Actions bottom sheet, launched from the 20% action button
// on the cart/scan bar. A thumb-reachable shortcut to the most common Functions
// so the cashier doesn't have to leave the current tab. Mounted once in
// POSLayout; open state lives in uiStore (isQuickActionsOpen). Every action
// reuses the SAME implementation as the Functions tab — hold/void hooks, global
// modals, print — so behaviour can't drift.
//
// Refund is intentionally absent for now: its bootstrap (createRefund ->
// enterRefundMode -> seedRefundPreview -> fetchProducts) needs the same
// hook-extraction treatment as void before it can be safely shared here.
export function QuickActionsSheet() {
  const isOpen = useUIStore((s) => s.isQuickActionsOpen)
  const close = useUIStore((s) => s.closeQuickActions)
  const openModal = useUIStore((s) => s.openModal)
  const showToast = useUIStore((s) => s.showToast)
  const showConfirm = useUIStore((s) => s.showConfirm)

  const { holdCurrent } = useHoldCurrentReceipt()
  const { voidCurrent } = useVoidCurrentReceipt()
  const printReceipt = usePrintReceipt()
  const selectCustomer = useSelectCustomer()
  const navigate = useNavigate()
  const { data: posConfig } = usePOSConfig()
  const allowDelivery = posConfig?.posAllowDelivery === true
  const [showAddCustomer, setShowAddCustomer] = useState(false)

  // Recall can't run on top of an in-progress cart (it would clobber the
  // current contents). Mirrors FunctionsTab.blockIfPendingTransaction.
  const blockIfPending = (proceed: () => void) => {
    if (usePOSStore.getState().cart.length > 0) {
      showConfirm({
        title: 'Pending Transaction',
        message:
          'Cannot complete this action while a transaction is pending. Please complete, hold, or void the current sale first.',
        confirmLabel: 'OK',
        cancelLabel: null,
        variant: 'danger',
        onConfirm: () => {},
      })
      return
    }
    proceed()
  }

  const handleReprint = () => {
    const receiptId = usePOSStore.getState().lastCompletedReceiptId
    if (!receiptId) {
      showToast({ type: 'warning', title: 'No Receipt', message: 'No completed receipt to reprint.' })
      return
    }
    printReceipt.mutate(
      { receiptId, isCopy: true },
      {
        onSuccess: () => showToast({ type: 'success', title: 'Receipt Sent', message: 'Reprint sent to printer.' }),
        onError: () => showToast({ type: 'error', title: 'Print Failed', message: 'Could not send receipt to printer.' }),
      },
    )
  }

  const handleCustomerSuccess = (customer: Customer) => {
    selectCustomer.mutate(customer, { onSuccess: () => setShowAddCustomer(false) })
  }

  // Close the sheet first, then run the action (which may itself open a modal).
  const act = (fn: () => void) => {
    close()
    fn()
  }

  const moreActions = [
    { id: 'recall', label: 'Recall hold', icon: RotateCcw, tone: 'bg-green-500', run: () => blockIfPending(() => openModal('held-receipts')) },
    { id: 'reprint', label: 'Reprint', icon: Printer, tone: 'bg-gray-500', run: handleReprint },
    { id: 'discount', label: 'Discount', icon: Percent, tone: 'bg-green-500', run: () => openModal('discount') },
    { id: 'giftcard', label: 'Gift card', icon: Gift, tone: 'bg-purple-500', run: () => openModal('gift-card-sell') },
    { id: 'customer', label: 'Customer', icon: UserPlus, tone: 'bg-blue-500', run: () => setShowAddCustomer(true) },
    // Delivery hub — only when the store has delivery enabled (same gate as
    // the Orders > Pickup/Delivery tab and the Deliveries board).
    ...(allowDelivery
      ? [{ id: 'delivery', label: 'Delivery', icon: Truck, tone: 'bg-blue-600', run: () => navigate({ to: '/deliveries' }) }]
      : []),
  ]

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40 animate-fade-in"
          onClick={close}
        >
          <div
            className="bg-white rounded-t-2xl shadow-xl px-4 pt-3 animate-slide-up"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-gray-300" />

            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
              Quick actions
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => act(holdCurrent)}
                className="flex flex-col items-center justify-center gap-1.5 py-5 rounded-2xl bg-amber-500 active:bg-amber-600 text-white font-semibold"
              >
                <Pause className="w-6 h-6" />
                Hold receipt
              </button>
              <button
                type="button"
                onClick={() => act(voidCurrent)}
                className="flex flex-col items-center justify-center gap-1.5 py-5 rounded-2xl bg-red-500 active:bg-red-600 text-white font-semibold"
              >
                <Ban className="w-6 h-6" />
                Void receipt
              </button>
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-5 mb-2">
              More functions
            </p>
            <div className="grid grid-cols-3 gap-2.5">
              {moreActions.map((a) => {
                const Icon = a.icon
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => act(a.run)}
                    className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border border-gray-200 active:bg-gray-50"
                  >
                    <span className={`w-10 h-10 rounded-xl ${a.tone} flex items-center justify-center text-white`}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="text-xs font-medium text-gray-700">{a.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {showAddCustomer && (
        <AddCustomerModal
          onClose={() => setShowAddCustomer(false)}
          onSuccess={handleCustomerSuccess}
        />
      )}
    </>
  )
}
